import { FieldModel } from '@cibo/core'
import { QUERY_KEY } from '@cibo/mapgl'
import AddCircle from '@mui/icons-material/AddCircleOutline'
import DeleteIcon from '@mui/icons-material/Delete'
import {
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { BoundaryThumbnail, useLandManagement } from '../..'

export const Fieldset = ({
  fieldsetFields,
  removeFieldsetField,
  setSensingDialogOpen,
  setCreatingNew,
}: {
  fieldsetFields: FieldModel[]
  removeFieldsetField: (fieldId: string) => void
  setSensingDialogOpen?: (open: boolean) => void
  setCreatingNew: (open: boolean) => void
}) => {
  const theme = useTheme()
  const queryClient = useQueryClient()
  const { t } = useTranslation('LandManager/features/CreateLand')
  const { setDeleteModal } = useLandManagement()

  return (
    <Stack justifyContent="space-between" sx={{ height: '100%' }}>
      <Stack>
        <Stack spacing={1} padding={2}>
          <Typography variant="h6">{t('workingFields')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('workingFieldsSubhead')}
          </Typography>
        </Stack>
        <List
          sx={{
            width: '100%',
            bgcolor: 'background.paper',
            paddingBlock: 1,
          }}
        >
          {fieldsetFields.map((field: FieldModel, index) => (
            <Fragment key={field.id}>
              {index > 0 && <Divider />}
              <ListItem
                sx={{
                  pointerEvents: 'all',
                  '.hoverShow': { display: 'none' },
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.secondary.main, 0.04),
                    '.hoverShow': {
                      display: 'block',
                    },
                  },
                }}
              >
                <ListItemAvatar>
                  <BoundaryThumbnail
                    boundary={{
                      areaDef: [{ feature: field.feature, type: 'feature' }],
                      name: field.name,
                    }}
                    height={36}
                    width={36}
                    style={{ borderRadius: 0.5 }}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={field.name}
                  secondary={[field.ownerLabel, t('acres', { count: field.acres })].join(', ')}
                  sx={{ flex: 3, wordWrap: 'break-word' }}
                />
                <Stack sx={{ flex: 1 }} direction="row">
                  {/* will be re-implemented in future, when edit is compatible */}
                  {/*  <IconButton
                    style={{ pointerEvents: 'all' }}
                    aria-label="edit"
                    onClick={() => console.log('edit')}
                    size="small"
                    className="hoverShow"
                  >
                    <EditNotifications sx={{ height: '20px', width: '20px' }} />
                  </IconButton>*/}

                  <IconButton
                    className="hoverShow"
                    style={{ pointerEvents: 'all' }}
                    edge="end"
                    aria-label="delete"
                    size="small"
                    onClick={() =>
                      setDeleteModal({
                        open: true,
                        selectedIds: [field.id],
                        onSuccess: () => {
                          removeFieldsetField(field.id)
                          queryClient.invalidateQueries([QUERY_KEY.FIELDS_MAP_LAYER])
                        },
                      })
                    }
                  >
                    <DeleteIcon color="error" sx={{ height: '20px', width: '20px' }} />
                  </IconButton>
                </Stack>
              </ListItem>
            </Fragment>
          ))}
          <Divider />
          <ListItem
            disablePadding
            sx={{
              paddingTop: 2,
              paddingBottom: 1,
              paddingInline: 2,
            }}
          >
            <Button
              onClick={() => setCreatingNew(true)}
              color="secondary"
              startIcon={<AddCircle />}
              data-testid="addFieldBtn"
            >
              {t('addAnotherField')}
            </Button>
            <Divider />
          </ListItem>
        </List>
      </Stack>
      <Stack>
        <Divider />
        <Button
          sx={{ m: 3 }}
          variant="contained"
          color="primary"
          data-testid="continueFieldCreation"
          onClick={() => setSensingDialogOpen && setSensingDialogOpen(true)}
        >
          {t('continue')}
        </Button>
      </Stack>
    </Stack>
  )
}
